import React, { useEffect, useState } from "react";
import Navbar from "../components/Cripsr/NavbarCrispr";
import NarrativeSection from "../components/phones/NarrativeSection";
import EndOfStoryNav from "../components/phones/EndOfStoryNav";
import BecauseScienceFooter from "../components/phones/BecauseScienceFooter";

// 🔁 Swap these with your actual assets when you have them
import foeHero from "../assets/images/hero/Hero Images-04.png";                  // big yellow pattern hero
import teensChart from "../assets/images/charts/psychology-charts_6.jpg";      // chart image
import dougPhoto from "../assets/images/chartoptions/Doug Kenrick.jpeg";         // photo image

// thumbs for the “More from this episode” / Next Chapter
import notHangingChart from "../assets/images/charts/psychology-charts_2.jpg";
import morelikelytofeel from "../assets/images/charts/psychology-charts_1.jpg";

import stagRabbit1 from '../assets/images/chartoptions/Stag and Rabbit-01.png'
import stagRabbit2 from '../assets/images/chartoptions/Stag and Rabbit-02.png'


import ArticleCredits from "../components/phones/ArticleCredits";


// Replace these thumbs/banners with your real assets when ready
import phonesThumb from "../assets/images/hero/Hero Images.png";
import siliconThumb from "../assets/images/hero/Hero Images-02.png";
import gutThumb from "../assets/images/hero/Hero Images-03.png";
import privacyThumb from "../assets/images/hero/Hero Images-01.png";
import rechargingThumb from "../assets/images/hero/Hero Images-05.png";

const navLinks = [
    { label: "We love our phones", href: "/phones" },
    { label: "From stone to silicon", href: "/phones" },
    { label: "Friend or foe", href: "/phones/friendorfoe" },
    { label: "The privacy burden", href: "/phones/privacyburden" },
    { label: "Gut check", href: "/phones/gutcheck" },
    { label: 'Recharging', href: '/phones/recharging' },
];

export default function FriendOrFoePage() {
    const [isShrunk, setIsShrunk] = useState(false);
    useEffect(() => {
        const onScroll = () => setIsShrunk(window.scrollY > 0);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <div className="relative w-full bg-white">
            {/* Navbar */}
            <Navbar isShrunk={isShrunk} navLinks={navLinks} />

            {/* HERO + audio + opener */}
            <NarrativeSection
                id="foe"
                containerWidth="max-w-md"
                padY="py-10"
                centeredImage={{
                    src: foeHero,
                    alt: "Friend or foe hero",
                    widthClass: "w-[90%] sm:w-[85%]",
                }}
                // no big heading — matches your reference layout
                content={[
                    // inline audio block (keeps components unchanged)
                    <div key="audio" className="text-center mb-8">
                        <p className="uppercase text-gray-500 text-xs tracking-wide mb-2">
                            Listen to this episode
                        </p>
                        <div className="flex justify-center">
                            <audio controls className="w-full max-w-md">
                                <source src="/audio/friend-or-foe.mp3" type="audio/mpeg" />
                                Your browser does not support the audio element.
                            </audio>
                        </div>
                    </div>,

                    `For 1,078 days straight (and counting), Kaya, 16, has opened up Snapchat on her phone and sent a message to her friend across town.`,
                    `Her friend has done the same.`,
                    `This is how they communicate.`,
                    `And they haven’t missed a day since the sixth grade.`,
                    `“When I wake up, I look at my phone because I need to send streaks on Snapchat or reply to somebody that was having a conversation with the night before,” said Kaya, a freshman at East Mesa High School. “On Snapchat, you’re able to show friends and you can just talk to your friends one-on-one there, so usually every single day I’ll wake up and I’ll talk to my friends.”`,
                    {
                        type: "quote",
                        content:
                            "When I wake up, I look at my phone because I need to send streaks on Snapchat...",
                        tone: "primary",
                        align: "left",
                    },
                    `Touted as powerful social tools, apps like Snapchat, Instagram and Facebook have opened the floodgates to information and moving seams of real-time opportunities for people to connect.`,
                    `But with endless information a swipe or click away, are these apps reshaping how we connect and talk to people? Are they creating more community or causing more stress?`,
                ]}
            />

            <figure key="friends-chart" className="flex justify-center my-8">
                <div className="relative w-[92%] sm:w-[40%]">
                    {/* blue offset border behind */}
                    <div className="absolute -bottom-3 -right-2 w-full h-full  bg-blue-600 rounded-sm" />
                    {/* main framed image */}
                    <img
                        src={teensChart}
                        alt="Not Hanging Out with Friends — times per week, teens checking in vs. hanging out"
                        className="relative w-full border-2 border-blue-600 rounded-sm bg-white"
                    />

                </div>
            </figure>,

            {/* Chart + analysis */}
            <NarrativeSection
                containerWidth="max-w-md"
                padY="py-8"
                content={[
                    // Chart figure (no extra component)


                    `“Social media meets a human need to connect with others, to feel belonging and support and it’s so readily available,” said Matt Meier, a clinical psychologist and mental health and substance abuse addiction specialist. “That nonstop access can positively lead to addiction, but it can also lead to much more severe problems as well as what we used to have without this technology.”`,
                    `While the jury is still out on causal influences of social media use on our daily lives, psychologists and social science researchers for years have been closely studying the links of how our electronic devices and social media apps are influencing our relationships — for better and for worse.`,
                    `Doug Kenrick, an evolutionary psychology researcher and father to a teen who enjoys connecting with friends through online video games, is conducting research with doctoral student Cameron Bruner to understand how technology affects already established fundamental human social motivations.`,
                    // Photo with caption
                    <figure key="photo" className="flex justify-center mb-6">
                        <div className="w-[90%] sm:w-[100%]">
                            <img
                                src={dougPhoto}
                                alt="Douglas Kenrick and son David Laughing-Kenrick"
                                className="w-full rounded-sm shadow-sm"
                            />
                            <figcaption className="mt-2 text-sm text-gray-600 italic">
                                Douglas Kenrick (left) and son David Lundberg-Kenrick (left) study the intersection of evolutionary biology and social psychology.
                            </figcaption>
                        </div>
                    </figure>,


                ]}
            />
            <NarrativeSection
                containerWidth="max-w-md"
                padY="py-8"
                content={[
                    `Evolutionarily, all humans have motivational systems that guided behavior throughout our lifespan. Today, modern technology sometimes “potentizes” those motivations across a spectrum — occasionally taking advantage of them, and sometimes enhancing prosocial ones.`,
                    `For example, having friends or human affiliation is essential to human evolution. Being affiliated with others helped ancestors create alliances to help with food gathering and protection. Today, technology and social media prey on affiliation motives — they offer a way to connect but also take our time and our data.`,
                    `“Kids need to make friends and we’re here because our ancestors were very good at making personal connections,” Kenrick said. “When we talk about what these machines do for us, sometimes these machines are in a mutualistic relationship with us, they’re giving us something but at other times, they’re taking something.”`,

                    // Chart figure with blue offset border like your screenshot



                ]}
            />
            <figure key="friends-chart" className="flex justify-center my-8">
                <div className="relative w-[92%] sm:w-[40%]">
                    {/* blue offset border behind */}
                    <div className="absolute -bottom-3 -right-2 w-full h-full  bg-blue-600 rounded-sm" />
                    {/* main framed image */}
                    <img
                        src={notHangingChart}
                        alt="Not Hanging Out with Friends — times per week, teens checking in vs. hanging out"
                        className="relative w-full border-2 border-blue-600 rounded-sm bg-white"
                    />

                </div>
            </figure>,

            <NarrativeSection
                containerWidth="max-w-md"
                padY="py-8"
                content={[
                    `“I do worry, though, that the new generation of kids are not going to make it because their electronic devices are not always serving their needs. There are worrying correlations — devices are parasitizing in the sense of taking way more than they’re giving.” As one example, students who spend more hours racking up points playing computer games have fewer hours left to study, and consequently, get lower grades.`,
                    `A 2021 study published in the Journal of Adolescence on teen and loneliness found that the psychological well-being of adolescents in schools around the world began to decline after 2012, in conjunction with the rise of smartphone access and increased internet use.`,
                    `Whether it's responsible or not, loneliness went up in teens after the iPhone was introduced`,


                ]}
            />

            <figure key="friends-chart" className="flex justify-center my-8">
                <div className="relative w-[92%] sm:w-[40%]">
                    {/* blue offset border behind */}
                    <div className="absolute -bottom-3 -right-2 w-full h-full  bg-blue-600 rounded-sm" />
                    {/* main framed image */}
                    <img
                        src={morelikelytofeel}
                        alt="Not Hanging Out with Friends — times per week, teens checking in vs. hanging out"
                        className="relative w-full border-2 border-blue-600 rounded-sm bg-white"
                    />

                </div>
            </figure>,
            <NarrativeSection
                containerWidth="max-w-md"
                padY="py-8"
                content={[
                    `The authors of the study, psychologists Jonathan Haidt and Jean M. Twenge, wrote in the New York Times, “If anyone has another explanation for the global increase in loneliness at school, we’d love to hear it.”`,
                    `Kenrick, a professor of psychology at Arizona State University, also points out that a number of studies have shown that increased social media use correlates with increased depression.`,
                    `“If you look at the amount of time that kids spend on social media, the ones who spend more than average are unhappier than average,” Kenrick said. “And it turns out that kids who spend more time face to face with friends are happier than average. It looks like these machines aren’t doing what we want them to do.”`,


                ]}
            />
            <figure key="friends-chart" className="flex justify-center my-8">
                <div className="relative w-[92%] sm:w-[40%]">
                    {/* blue offset border behind */}
                    <div className="absolute -bottom-3 -right-2 w-full h-full  bg-blue-600 rounded-sm" />
                    {/* main framed image */}
                    <img
                        src={notHangingChart}
                        alt="Not Hanging Out with Friends — times per week, teens checking in vs. hanging out"
                        className="relative w-full border-2 border-blue-600 rounded-sm bg-white"
                    />

                </div>
            </figure>,

            <NarrativeSection
                containerWidth="max-w-md"
                padY="py-8"
                content={[
                    `Other researchers have also pointed out that with the rise of smartphone use—coupled with the restlessness associated with constantly wanting something new—in-person interactions have become a lot harder to coordinate.`,
                    `Athena Aktipis, a social psychologist who specializes in coordination and cooperation between people, says that research has shown with increased screen use, you can miss opportunities for new in-person connections.`,

                    // small centered bunny illustration
                    <figure key="bunny-1" className="flex justify-center my-6">
                        <img src={stagRabbit1} alt="" className="w-[50%] sm:w-[60%]" />
                    </figure>,

                    `“Because you have all of these small but rewarding things that you can do on your phone while having a conversation, deepening a friendship might not be as immediately rewarding even though in the long term it’s often going to be more valuable,” said Aktipis.`,
                    `“There’s this dilemma when we’re sitting down at a table and we both have our phones and we’re scrolling but we’re together. It can be very hard to put those away and say, ‘Okay, now let’s have a conversation.’”`,
                    `Aktipis says this phenomena is akin to a common social science game theory called the ‘stag hunt game dilemma’: Do you go for the smaller reward, the satisfaction you feel from scrolling through social media (rabbit), or do you go for the bigger reward of putting down your phone and engaging in face-to-face conversation (stag).`,
                    `“It’s a safer option to hunt rabbits but it’s a lower payoff; hunting stag is a higher payoff but requires that you coordinate with others,” Aktipis said. “This social media dilemma maps right into this classic game theoretic scenario.”`,

                    // second bunny illustration
                    <figure key="bunny-2" className="flex justify-center my-6">
                        <img src={stagRabbit2} alt="" className="w-[50%] sm:w-[60%]" />
                    </figure>,


                ]}
            />
            <NarrativeSection
                id="making-decisions"
                containerWidth="max-w-md"
                padY="py-0"
                content={[
                    <h3 key="carrot" className="text-blue-700 font-bold text-xl sm:text-3xl mt-4">
                        Making decisions intentionally
                    </h3>,
                    `Psychology experts agree that while social media use can have its benefits, what’s critically important for the health of our relationships is understanding the potential consequences of social media use and making informed decisions on how we want to intentionally engage.`,
                    `Aktipis says that because our ancestors evolved in environments where we only had face-to-face interaction, we need to recognize that people using social media to connect are, in a sense, giving up the ability to create their own rules of engagement or true social world.`,
                    `“We're giving the rules over to social media platforms that are not necessarily there to make us feel good about ourselves and our social relationships,” said Aktipis. “You have to think about what are the incentives that underlie the various players.”`,
                    `For platforms like Facebook, Instagram and TikTok, there is an incentive to manipulate our attention in ways that keep our eyeballs on screens, even if coming back to those platforms makes us feel more anxious, worried, excluded or angry.`,
                    {
                        type: "quote",
                        content:
                            "I think of (my phone) almost as a best friend, but also kind of an enemy in the way where I don't want to be spending time with it as much as I do...",
                        tone: "primary",
                        align: "left",
                        className: "text-[18px] sm:text-lg",
                    },
                    `“That's very different from how you might design a system if you were thinking about putting mental health as the goal that makes people feel included, gives them a sense of belonging, helps them to have a more mindful approach to their lives, encourages them to exercise, and encourages them to have meaningful relationships with people and their household,” Aktipis said.`,
                    `Kenrick agrees and says that by recognizing technology is embedded into our lives we can use that knowledge to our benefit and selectively use our smartphones to feed us information, help us do meaningful things and work toward longer-term goals.`,
                    `With “stimulus control,” like setting app limits or engaging with social media only after completing a task, we can foster a mutually beneficial relationship with our smartphones.`,
                    `Kaya, the high school teenager, says she puts limits on her phone and that her relationships with her friends have not suffered because of it. But how often she uses her phone and how she continues to navigate her social media use, she says, is conflicted.`,
                    `“I think of (my phone) almost as a best friend, but also kind of an enemy in the way where I don't want to be spending time with it as much as I do,” Kaya said. “I do prefer not constantly being on my phone just because there are more things to do and I feel like I'm getting things done. But being on my phone here, it feels like it's a necessity to have.”`,
                    `“My friends and I use it but I’m aware of the consequences.”`, // 👈 added comma above fixes the error
                ]}
            />

            <ArticleCredits
                lines={[
                    "Written by by David Rozul",
                    "Illustrations by ___",
                    "Audio narration by David Rozul",
                    "Sound design by Christopher Goulet",
                ]}
            />


            {/* Next + More */}
            <EndOfStoryNav
                nextChapter={{
                    href: "/phones/privacyburden",
                    title: "The privacy burden",
                    imageSrc: privacyThumb, // replace with your banner if you have one
                    imageAlt: "Privacy burden banner",
                }}
                more={[
                    { href: "/phones", title: "We love our phones", imageSrc: phonesThumb },
                    { href: "/phones/stoneandsilicon", title: "From stone to silicon", imageSrc: siliconThumb },
                    { href: "/phones/gutcheck", title: "Gut check", imageSrc: gutThumb },
                    { href: "/phones/recharging", title: "Recharging", imageSrc: rechargingThumb },
                ]}
            />

            {/* Footer */}
            <BecauseScienceFooter />
        </div>
    );
}
